import { useState, ChangeEvent } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Divider,
  Stack,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Flight,
} from '@mui/icons-material';
import { theme } from '@/theme/theme';
import { abyssColors } from '@/theme/colors';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import Button from '@/components/shared/Button';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useProcessPdfForPoiMutation } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Pill } from '@/components/shared/Pill';

type Props = {
  open: boolean;
  onClose: () => void;
  onUploadComplete?: (files: UploadedFile[]) => void;
};

type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'error';

type UploadedFile = {
  id: string;
  file: File;
  status: FileUploadStatus;
  progress: number;
  error?: string;
};

const MAX_FILE_SIZE_MB = 50;
const MAX_FILES = 10;

export const SmartPoiImportModal = ({ open, onClose, onUploadComplete }: Props) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [processPdfForPoi] = useProcessPdfForPoiMutation();
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    if (file.type !== 'application/pdf') {
      return { valid: false, error: 'Only PDF files are allowed' };
    }

    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      return {
        valid: false,
        error: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`,
      };
    }

    return { valid: true };
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // Check total file count
    if (uploadedFiles.length + fileArray.length > MAX_FILES) {
      showSnackBar(`You can only upload a maximum of ${MAX_FILES} files`, 'error');
      return;
    }

    const newFiles: UploadedFile[] = [];
    const errors: string[] = [];

    fileArray.forEach((file) => {
      // Check for duplicates
      const isDuplicate = uploadedFiles.some((f) => f.file.name === file.name);
      if (isDuplicate) {
        errors.push(`${file.name} is already added`);
        return;
      }

      // Validate file
      const validation = validateFile(file);
      if (!validation.valid) {
        errors.push(`${file.name}: ${validation.error}`);
        return;
      }

      newFiles.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        status: 'pending',
        progress: 0,
      });
    });

    if (errors.length > 0) {
      errors.forEach((error) => showSnackBar(error, 'error'));
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }

    // Reset input
    event.target.value = '';
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.addEventListener('load', () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      });
      reader.addEventListener('error', () => reject(reader.error));
    });
  };

  const uploadFile = async (uploadedFile: UploadedFile): Promise<void> => {
    try {
      // Update status to uploading
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === uploadedFile.id ? { ...f, status: 'uploading', progress: 10 } : f
        )
      );

      // Convert file to base64
      const base64Data = await convertFileToBase64(uploadedFile.file);

      setUploadedFiles((prev) =>
        prev.map((f) => (f.id === uploadedFile.id ? { ...f, progress: 30 } : f))
      );

      // Call the mutation
      const { data, errors } = await processPdfForPoi({
        variables: {
          input: {
            structureId,
            pdfDocuments: [
              {
                fileName: uploadedFile.file.name,
                base64Data,
                mimeType: 'application/pdf',
              },
            ],
            lastModifiedFor: 'PDF Import',
          },
        },
      });

      setUploadedFiles((prev) =>
        prev.map((f) => (f.id === uploadedFile.id ? { ...f, progress: 90 } : f))
      );

      // Check for errors in the response
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }

      if (data?.processPdfForPoi?.errors && data.processPdfForPoi.errors.length > 0) {
        const errorMessage = data.processPdfForPoi.errors[0].message || 'Processing failed';
        throw new Error(errorMessage);
      }

      // Success
      setUploadedFiles((prev) =>
        prev.map((f) => (f.id === uploadedFile.id ? { ...f, status: 'success', progress: 100 } : f))
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed. Please try again.';
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === uploadedFile.id ? { ...f, status: 'error', progress: 0, error: errorMessage } : f
        )
      );
      throw error;
    }
  };

  const handleUpload = async () => {
    const pendingFiles = uploadedFiles.filter(
      (f) => f.status === 'pending' || f.status === 'error'
    );

    if (pendingFiles.length === 0) {
      showSnackBar('No files to upload', 'warning');
      return;
    }

    if (!structureId) {
      showSnackBar('Structure ID not found', 'error');
      return;
    }

    setIsUploading(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      // Upload files sequentially
      for (const file of pendingFiles) {
        try {
          await uploadFile(file);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to upload ${file.file.name}:`, error);
        }
      }

      // Show summary message
      if (successCount > 0 && errorCount === 0) {
        showSnackBar(`Successfully processed ${successCount} file(s)`, 'success');
      } else if (successCount > 0 && errorCount > 0) {
        showSnackBar(
          `Processed ${successCount} file(s) successfully, ${errorCount} failed`,
          'warning'
        );
      } else {
        showSnackBar('All uploads failed', 'error');
      }

      if (onUploadComplete && successCount > 0) {
        const successfulFiles = uploadedFiles.filter((f) => f.status === 'success');
        onUploadComplete(successfulFiles);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) {
      showSnackBar('Please wait for uploads to complete', 'warning');
      return;
    }
    setUploadedFiles([]);
    onClose();
  };

  const getStatusIcon = (status: FileUploadStatus) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ color: abyssColors.success[500] }} />;
      case 'error':
        return <ErrorIcon sx={{ color: abyssColors.error[500] }} />;
      case 'uploading':
        return <FileIcon sx={{ color: abyssColors.primary[500] }} />;
      default:
        return <FileIcon sx={{ color: theme.palette.text.secondary }} />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const allFilesProcessed =
    uploadedFiles.length > 0 &&
    uploadedFiles.every((f) => f.status === 'success' || f.status === 'error');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2, minHeight: '500px' } }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap="0.8rem">
            <Typography variant="h6" fontWeight={600}>
              Import from FMR Documents
            </Typography>
            <Pill
              label="Pilot"
              status="linked"
              size="small"
              state="hover"
              disabled
              showIcon
              icon={<Flight />}
            />
          </Stack>
          <IconButton onClick={handleClose} disabled={isUploading} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {/* Upload Area */}
        <Box
          sx={{
            border: `2px dashed ${abyssColors.primary[200]}`,
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: abyssColors.primary[50],
            mb: 3,
            cursor: uploadedFiles.length >= MAX_FILES ? 'not-allowed' : 'pointer',
            opacity: uploadedFiles.length >= MAX_FILES ? 0.5 : 1,
            '&:hover': {
              backgroundColor:
                uploadedFiles.length >= MAX_FILES
                  ? abyssColors.primary[50]
                  : abyssColors.primary[100],
            },
          }}
        >
          <input
            accept="application/pdf"
            style={{ display: 'none' }}
            id="pdf-upload-input"
            multiple
            type="file"
            onChange={handleFileSelect}
            disabled={uploadedFiles.length >= MAX_FILES || isUploading}
          />
          <label htmlFor="pdf-upload-input">
            <Box sx={{ cursor: uploadedFiles.length >= MAX_FILES ? 'not-allowed' : 'pointer' }}>
              <AbyssIcon iconName="file-upload" size="2xl" />
              <Typography variant="body1" fontWeight={500} gutterBottom marginTop="1.6rem">
                Click to upload or drag and drop
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PDF files only (max {MAX_FILE_SIZE_MB}MB per file)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Maximum {MAX_FILES} files ({uploadedFiles.length}/{MAX_FILES} selected)
              </Typography>
            </Box>
          </label>
        </Box>

        {/* File List */}
        {uploadedFiles.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Selected Files ({uploadedFiles.length})
            </Typography>
            <List sx={{ maxHeight: '300px', overflow: 'auto' }}>
              {uploadedFiles.map((uploadedFile) => (
                <ListItem
                  key={uploadedFile.id}
                  sx={{
                    border: `1px solid ${abyssColors.primary[200]}`,
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: 'background.paper',
                  }}
                  secondaryAction={
                    uploadedFile.status !== 'uploading' && (
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFile(uploadedFile.id)}
                        disabled={isUploading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemIcon>{getStatusIcon(uploadedFile.status)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {uploadedFile.file.name}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(uploadedFile.file.size)}
                          {uploadedFile.status === 'uploading' &&
                            ` • ${Math.round(uploadedFile.progress)}%`}
                          {uploadedFile.status === 'success' && ' • Uploaded'}
                          {uploadedFile.status === 'error' &&
                            ` • ${uploadedFile.error || 'Failed'}`}
                        </Typography>
                        {uploadedFile.status === 'uploading' && (
                          <LinearProgress
                            variant="determinate"
                            value={uploadedFile.progress}
                            sx={{ mt: 1, height: 6, borderRadius: 1 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {uploadedFiles.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No files selected. Click the upload area to select PDF files.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isUploading} variant="outlined">
          {allFilesProcessed ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploadedFiles.length === 0 || isUploading || allFilesProcessed}
          startIcon={<AbyssIcon iconName="file-upload-line" />}
        >
          {isUploading ? 'Uploading...' : 'Upload Files'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
